/** Cultura por defecto. Cuando existan más idiomas, resolveCulture validará contra languageOptions. */
export const DEFAULT_CULTURE = 'ES'

export type LanguageOption = { nombreCultura: string; id: number }

export function getLanguageOptions(): LanguageOption[] {
  try {
    const raw = window.localStorage.getItem('languageOptions')
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

/** Normaliza y valida cultura; si no existe en el catálogo, devuelve DEFAULT_CULTURE. */
export function resolveCulture(cultura: string | number | null | undefined): string {
  if (cultura == null || cultura === '') return DEFAULT_CULTURE

  const normalized =
    typeof cultura === 'number' ? String(cultura) : String(cultura).trim().toUpperCase()

  if (!normalized) return DEFAULT_CULTURE

  const options = getLanguageOptions()

  if (options.length === 0) {
    // Sin catálogo cacheado: solo aceptamos códigos cortos tipo ES, EN, FR…
    return /^[A-Z]{2}$/.test(normalized) ? normalized : DEFAULT_CULTURE
  }

  const byCulture = options.find(
    (o) => o.nombreCultura?.toUpperCase() === normalized,
  )
  if (byCulture) return byCulture.nombreCultura

  const asNumber = Number(normalized)
  if (!Number.isNaN(asNumber)) {
    const byId = options.find((o) => o.id === asNumber)
    if (byId) return byId.nombreCultura
  }

  return DEFAULT_CULTURE
}

export function getAcceptLanguageHeader(cultura?: string | number | null): string {
  return resolveCulture(cultura)
}

const MIN_TEXTOS_KEYS = 20

/** Comprueba que el bundle del API tiene traducciones reales, no solo claves vacías. */
export function hasValidTextosBundle(
  textos: Record<string, string> | null | undefined,
): boolean {
  if (!textos || typeof textos !== 'object') return false

  const keys = Object.keys(textos)
  if (keys.length < MIN_TEXTOS_KEYS) return false

  const sample = textos.MSG_1 ?? textos.MSG_67
  return typeof sample === 'string' && !sample.startsWith('MSG_')
}

const I18N_DIAGNOSTIC_SAMPLES = ['MSG_67', 'MSG_268', 'MSG_540'] as const

let lastI18nDiagnosticLog: { count: number; culture: string } | null = null

/** Log temporal para validar carga de traducciones en cliente. Eliminar tras confirmar el fix. */
export function logI18nDiagnostics(
  textos: Record<string, string> | null | undefined,
  culture: string,
): void {
  const keys = Object.keys(textos ?? {})
  const count = keys.length

  if (lastI18nDiagnosticLog?.count === count && lastI18nDiagnosticLog?.culture === culture) {
    return
  }
  lastI18nDiagnosticLog = { count, culture }

  const samples = Object.fromEntries(
    I18N_DIAGNOSTIC_SAMPLES.map((key) => [key, textos?.[key] ?? '(missing)']),
  )
  const valid = hasValidTextosBundle(textos)

  const status = {
    count,
    culture,
    valid,
    samples,
    at: new Date().toISOString(),
  }

  const w = window as Window & { __i18nStatus?: typeof status }
  w.__i18nStatus = status
}
